<template>
  <div class="container !pt-0">
    <el-form
      ref="integrationForm"
      :model="integrationForm"
      :rules="formRules"
      label-position="top"
      label-width="120px"
      @submit.native.prevent>
      <!-- Integration Name -->
      <el-row class="mb-6" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Integration Name" prop="name">
            <el-input
              v-model="integrationForm.name"
              class="gray"
              name="sendfox-name"
              maxlength="255"/>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- SendFox OAuth Token -->
      <el-row class="mb-4" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Personal Access Token" prop="sendfox_integration.oauth_token">
            <el-input
              v-model="integrationForm.sendfox_integration.oauth_token"
              class="gray"
              name="sendfox-oauth-token"
              autocomplete="off"
              maxlength="1200"
              @blur="onApiCredentialsChange"/>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Choose a List (Dropdown) -->
      <el-row v-if="apiCredentialsProvided" class="mb-4" :gutter="20">
        <el-col :span="24">
          <el-form-item label="SendFox List" prop="sendfox_integration.list_id">
            <el-select
              v-model="integrationForm.sendfox_integration.list_id"
              class="gray"
              placeholder="Select a SendFox List"
              :loading="listsLoading"
              :disabled="!sendfoxAuthorized || listsLoading"
              @change="onListChange">
              <el-option
                v-for="list in sendfoxLists"
                :key="list.id"
                :label="list.name"
                :value="String(list.id)"/>
            </el-select>
            <div v-if="listsLoading" class="loading-indicator">
              <Loader />
            </div>
            <div v-else-if="!listsLoading && sendfoxLists.length === 0" class="no-lists">
              No lists found. Please verify your Personal Access token.
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Field Mapping Section -->
      <div v-if="integrationCreated && apiCredentialsProvided && sendfoxAuthorized">
        <h3 class="mt-4 mb-2">Field Mapping</h3>
        <el-row class="mb-1" :gutter="20">
          <el-col :span="9">
            <el-form-item>
              <div slot="label" class="text-body-black">
                Internal Field
                <el-popover
                  placement="bottom"
                  trigger="hover"
                  content="These are the form fields from your widget">
                  <strong slot="reference"><i class="el-icon-info"></i></strong>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="2">
            <i class="el-icon-right text-xl opacity-0"></i>
          </el-col>
          <el-col :span="1">
            <el-checkbox v-model="allFieldsActive" @change="setAllFieldsActive(allFieldsActive)" />
          </el-col>
          <el-col :span="11" :offset="1">
            <el-form-item>
              <div slot="label" class="text-body-black">
                External Field
                <el-popover
                  placement="bottom"
                  trigger="hover"
                  content="These are the field names on SendFox. Email is mandatory.">
                  <strong slot="reference"><i class="el-icon-info"></i></strong>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <hr class="my-2" />

        <!-- Loop over each mapped field -->
        <el-row
          v-for="(mapping, index) in integrationForm.mapping"
          :key="index"
          :gutter="20"
          class="mb-2"
          type="flex"
          align="middle">
          <el-col :span="9">
            <span class="text-base">{{ mapping.internal.name }}</span>
          </el-col>
          <el-col :span="2">
            <i class="el-icon-right text-xl"></i>
          </el-col>
          <el-col :span="1">
            <el-checkbox v-model="mapping.active" @change="checkAllFieldsActive" />
          </el-col>
          <el-col :span="11" :offset="1">
            <el-form-item>
              <el-select
                v-model="mapping.external.name"
                class="gray"
                :disabled="!mapping.active"
                placeholder="Select SendFox Field">
                <el-option
                  v-for="field in sendfoxFields"
                  :key="field.id"
                  :label="field.name"
                  :value="field.id"
                  :disabled="isFieldMapped(field.id)"/>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- Submit Button -->
      <div class="mt-8 flex justify-end">
        <button class="btn secondary outline big mr-4" @click="cancel">
          Cancel
        </button>
        <button class="btn primary big" @click="saveChanges">
          {{ integrationCreated ? 'Done' : 'Save' }}
        </button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import integrationAPI from '@api/integration'

/* eslint-disable camelcase, no-unused-vars */
export default {
  name: 'SendFox',
  data() {
    return {
      enableUpdate: false,
      integrationCreated: false,
      integrationForm: {
        widget: null,
        name: 'SendFox',
        mapping: [],
        target: 'SENDFOX',
        sendfox_integration: {
          oauth_token: '',
          list_id: '',
        },
      },
      formRules: {
        name: [
          { required: true, message: 'Integration name is required', trigger: 'blur' },
          { max: 255, message: 'Integration name must be less than 255 characters', trigger: 'blur' },
        ],
        'sendfox_integration.oauth_token': [
          { required: true, message: 'Personal Access Token is required', trigger: 'blur' },
          { max: 1200, message: 'Personal Access Token must be less than 1200 characters', trigger: 'blur' },
        ],
        'sendfox_integration.list_id': [
          { required: true, message: 'SendFox List is required', trigger: 'change' },
        ],
      },

      // Additional states for advanced functionalities
      formModified: false,
      sendfoxFields: [],     // store retrieved contact properties (fields) for mapping
      sendfoxLists: [],      // store retrieved lists
      allFieldsActive: false,
      listsLoading: false,   // indicates if lists are being fetched
      sendfoxAuthorized: false, // indicates if SendFox is authorized (valid token)
      apiCredentialsProvided: false, // indicates if OAuth Token is provided
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID']),
    ...mapGetters('campaign/widget', ['getWidgetFields']),

    // The "integration" from Vuex store
    integration() {
      return this.$store.getters['integration/targetIntegration']
    },
  },
  watch: {
    // Watch for changes in the entire integrationForm
    integrationForm: {
      handler() {
        if (this.enableUpdate) {
          this.formModified = true
          this.$emit('form-modified', this.formModified)
        }
      },
      deep: true,
    },
  },
  created() {
    // Initialize form with existing integration (if any)
    this.setFormValues(this.integration)

    // If there's an existing integration, we mark it as created
    if (this.targetIntegrationID) {
      this.integrationCreated = true
      // If integration exists, attempt to authorize and fetch lists
      if (this.integrationForm.sendfox_integration.oauth_token) {
        this.apiCredentialsProvided = true
        this.fetchSendFoxLists()
      }
    }

    // Allow updates after the form has been initialized
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    /**
     * Sets up the initial form values from an existing integration (if present)
     */
    setFormValues(integration) {
      if (integration) {
        this.integrationForm.name = integration.name || 'SendFox'
        this.integrationForm.sendfox_integration.oauth_token =
            (integration.sendfox_integration?.oauth_token) || ''
        this.integrationForm.sendfox_integration.list_id =
            (integration.sendfox_integration?.list_id) || ''
        this.integrationForm.mapping = Array.isArray(integration.mapping)
            ? JSON.parse(JSON.stringify(integration.mapping))
            : []
      }
      // Set widget ID from store
      this.integrationForm.widget = this.$store.state.campaign.widget.targetWidgetID

      // Ensure mapping is in sync with widget fields
      this.syncWidgetFieldsWithMapping()
    },

    /**
     * Syncs the widget fields from the store with our integrationForm.mapping
     */
    syncWidgetFieldsWithMapping() {
      const widgetFields = this.getWidgetFields
      this.integrationForm.mapping = widgetFields.map((field) => {
        // Try to find an existing mapping for this field
        const existingMapping = this.integrationForm.mapping.find(
            (m) => m.internal.fieldID === field.id
        )
        // If none, create a new blank mapping
        return (
            existingMapping || {
              internal: { name: field.settings.label, fieldID: field.id },
              external: { name: '' },
              active: false,
            }
        )
      })
      // Update the "all fields active" checkbox
      this.checkAllFieldsActive()
    },

    /**
     * Called when the user toggles "allFieldsActive"
     */
    setAllFieldsActive(active) {
      this.integrationForm.mapping.forEach((item) => {
        item.active = active
      })
    },

    /**
     * Check if *all* fields are active
     */
    checkAllFieldsActive() {
      this.allFieldsActive = this.integrationForm.mapping.every((item) => item.active === true)
    },

    /**
     * Called when the user presses "Save" or "Done"
     */
    saveChanges() {
      this.$refs.integrationForm.validate((valid) => {
        if (!valid) return

        // Ensure all active mappings have an external field
        this.integrationForm.mapping.forEach((mapping) => {
          if (mapping.active && !mapping.external.name) {
            mapping.active = false
          }
        })

        // Decide which Vuex action to dispatch
        const saveAction = this.integrationCreated
            ? 'integration/updateIntegration'
            : 'integration/createIntegration'

        this.$store
            .dispatch(saveAction, this.integrationForm)
            .then(() => {
              this.$message.success('Save successful!')
              if (!this.integrationCreated) {
                // We just created the integration
                this.integrationCreated = true
                // If token was provided, try to fetch lists
                if (this.integrationForm.sendfox_integration.oauth_token) {
                  this.apiCredentialsProvided = true
                  this.fetchSendFoxLists()
                }
              } else {
                // Otherwise close
                this.$emit('force-close')
              }
            })
            .catch((error) => {
              this.$message.error('Failed to save integration.')
              console.error(error)
            })
      })
    },

    /**
     * Handler for the Cancel button
     */
    cancel() {
      if (this.formModified) {
        // If the form was touched, you might want a modal or a different approach
        this.$emit('close-drawer')
      } else {
        this.$emit('force-close')
      }
    },

    /**
     * Handler when the OAuth token field changes (on blur)
     */
    onApiCredentialsChange() {
      if (this.integrationCreated) {
        const { oauth_token, list_id } = this.integrationForm.sendfox_integration
        if (oauth_token && list_id) {
          this.apiCredentialsProvided = true
          this.fetchSendFoxLists()
        } else {
          this.apiCredentialsProvided = false
          this.resetSendFoxData()
        }
      }
    },

    /**
     * Resets the lists, fields, and mapped external fields
     */
    resetSendFoxData() {
      this.sendfoxLists = []
      this.integrationForm.sendfox_integration.list_id = ''
      this.sendfoxFields = []
      this.integrationForm.mapping.forEach((mapping) => {
        mapping.external.name = ''
      })
      this.sendfoxAuthorized = false
    },

    /**
     * Fetch all available SendFox lists from the server
     */
    fetchSendFoxLists() {
      const integrationId = this.integrationCreated ? this.integration.id : this.targetIntegrationID
      if (!integrationId) {
        this.$message.error('Integration ID is missing.')
        return
      }

      this.listsLoading = true

      integrationAPI
          .fetchSendFoxLists(integrationId)
          .then((response) => {
            this.sendfoxLists = response.data || []
            if (this.sendfoxLists.length > 0) {
              this.sendfoxAuthorized = true
              // Also fetch the custom fields for the selected list if any
              if (this.integrationForm.sendfox_integration.list_id) {
                this.fetchSendFoxFields(
                    this.integrationForm.sendfox_integration.list_id
                )
              }
            } else {
              this.sendfoxAuthorized = false
              this.$message.warning('No SendFox lists found for this token.')
            }
          })
          .catch((error) => {
            this.sendfoxAuthorized = false
            this.resetSendFoxData()
            this.$message.error('Failed to fetch SendFox lists. Please check your OAuth token.')
            console.error(error)
          })
          .finally(() => {
            this.listsLoading = false
          })
    },

    /**
     * Fetch SendFox custom fields (if available). Called after choosing a list.
     */
    fetchSendFoxFields(listId) {
      const integrationId = this.integrationCreated ? this.integration.id : this.targetIntegrationID
      if (!integrationId) {
        this.$message.error('Integration ID is missing.')
        return
      }

      if (!listId) {
        this.$message.warning('Please select a list to fetch fields.')
        this.sendfoxFields = []
        this.integrationForm.mapping.forEach((mapping) => {
          mapping.external.name = ''
        })
        return
      }

      integrationAPI
          .fetchSendFoxCustomFields(
              integrationId,
              listId
          )
          .then((response) => {
            // Suppose the response is { data: { custom_fields: [ {id, name}, ... ] } }
            this.sendfoxFields = response.data.custom_fields || []
            if (this.sendfoxFields.length === 0) {
              this.$message.warning('No custom fields found for the selected list.')
            }
          })
          .catch((error) => {
            this.sendfoxFields = []
            this.integrationForm.sendfox_integration.list_id = ''
            this.integrationForm.mapping.forEach((mapping) => {
              mapping.external.name = ''
            })
            this.$message.error('Failed to fetch SendFox custom fields.')
            console.error(error)
          })
    },

    /**
     * Handle when the user selects a new list
     */
    onListChange(selectedListId) {
      if (selectedListId) {
        this.fetchSendFoxFields(selectedListId)
      } else {
        this.sendfoxFields = []
        this.integrationForm.sendfox_integration.list_id = ''
        this.integrationForm.mapping.forEach((mapping) => {
          mapping.external.name = ''
        })
      }
    },

    /**
     * Determines if a field is already mapped (to avoid duplicates)
     */
    isFieldMapped(fieldValue) {
      return this.integrationForm.mapping.some(
          (mapping) => mapping.external.name === fieldValue && mapping.active
      )
    },
  },
}
</script>

<style scoped>
.loading-indicator {
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.no-lists {
  color: #999;
  font-size: 12px;
  margin-top: 5px;
}
</style>